import ColorInverter from '@/utils/color-inverter'
import { BaseSeratoFilter } from '@/pixi/filters/BaseSeratoFilter'
import * as PIXI from 'pixi.js'
import { EffectState } from '@/pixi/filters/types'

interface DuotoneUniforms {
  primaryColour: [number, number, number],
  secondaryColour: [number, number, number],
  duoAlpha: number
}

export class DuotoneFilter extends BaseSeratoFilter {
  get uniforms (): DuotoneUniforms {
    return super.uniforms as DuotoneUniforms
  }

  /**
   * @param primaryColour colour mapped to the darker parts in an image
   * @param secondaryColour colour mapped to the ligher parts in an image
   */
  constructor (effectState: EffectState, primaryColour: string, secondaryColour?: string) {
    const shader = `
    #define colourBlendThreshold 1.8 // The point where it is 50/50 each colour

    // colour params
    uniform vec3 primaryColour;
    uniform vec3 secondaryColour;
    uniform float duoAlpha; // defines how transparent the new colour is

    // generic default params
    varying vec2 vTextureCoord;
    uniform highp vec4 inputSize;
    uniform sampler2D uSampler;

    // convert from rgb to luma light level
    float luma(vec4 pixelColor) {
      return 0.2126 * pixelColor.r + 0.7152 * pixelColor.g + 0.0722 * pixelColor.b;
    }

    vec4 alphaComposite(vec4 base, vec4 blend)
    {
      vec4 colour;
      colour.a = blend.a + base.a * (1.0 - blend.a);
      colour.r = ((blend.r * blend.a) + base.r * base.a * (1.0 - blend.a)) / (colour.a);
      colour.g = ((blend.g * blend.a) + base.g * base.a * (1.0 - blend.a)) / (colour.a);
      colour.b = ((blend.b * blend.a) + base.b * base.a * (1.0 - blend.a)) / (colour.a);
      return colour;
    }

    void main(void)
    {
      // get the pixel color of the texture
      vec4 texturePixelColor = texture2D(uSampler, vTextureCoord);

      // pixel alpha is premultiplied, need to set it to black if transparent to be truely transparent
      if (texturePixelColor.a == 0.0) {
        gl_FragColor = vec4(0.0);
        return;
      }

      // greyscale the image
      vec3 greyScale = vec3(.5, .5, .5);
      vec4 texturePixelColorGreyScaled = vec4( vec3(dot( texturePixelColor.rgb, greyScale)), texturePixelColor.a);

      // get luma light level of the texture color
      float luma = luma(texturePixelColorGreyScaled);

      // mix 2 colours
      vec3 colour = mix(primaryColour, secondaryColour, luma);

      // Alpha blend the original with the duo
      colour = alphaComposite(texturePixelColor, vec4(colour, duoAlpha)).rgb;
      gl_FragColor = vec4(colour, texturePixelColor.a);
    }`

    const primaryColourRGB = PIXI.utils.hex2rgb(PIXI.utils.string2hex(primaryColour))
    const secondaryColourHex = secondaryColour ?? ColorInverter.invertHex(primaryColour)
    const secondaryColourRGB = PIXI.utils.hex2rgb(PIXI.utils.string2hex(secondaryColourHex))

    const uniforms: DuotoneUniforms = {
      primaryColour: [primaryColourRGB[0], primaryColourRGB[1], primaryColourRGB[2]],
      secondaryColour: [secondaryColourRGB[0], secondaryColourRGB[1], secondaryColourRGB[2]],
      duoAlpha: 0.5
    }

    super(
      effectState,
      undefined,
      shader,
      uniforms
    )
  }

  updateUniforms () {
    this.uniforms.duoAlpha = this.effectState.intensity * 0.85 + 0.15
  }
}
